import React, { useEffect, useMemo, useState } from "react";
import { countries } from "../../global/countriesAndSources";
import etoille from "../../media/star.png"
import "./GoogleCSE.css"
import useCurrentLanguage from "../../Helper/useCurrentLanguage";
import { getCountryPurchasePremium, getPurchasedCountriesByUser } from "../../HTTP/payment";
import { fetchAllRegions } from "../../HTTP/countries";
import { getIgo } from "../../HTTP/translation";
import { getIgoFromUser } from "../../HTTP/user";
import {
    isCountryPurchaseMarkerSubscription
} from "../../Helper/countryPurchaseAccess";

const WORLD_COUNTRY_CODE = "wd";
const frenchCountryDisplayNames = new Intl.DisplayNames(["fr"], { type: "region" });

export const CountryBar = (props) => {
    const localUser = useMemo(() => JSON.parse(localStorage.getItem("user") || "null"), []);
    const countrySelected = props.selectedCountry;
    const funChangeCountry = props.setSelectedCountry;
    const funChangeDisplayMap = props.displayMap;
    const selectedIgo2 = props.selectedIgo;
    const selectedRegion = props.selectedRegion || null;
    const onSelectRegion = props.setSelectedRegion;
    const isMobile = props.isMobile;
    const [countriesToRender, setCountriesToRender] = useState([])
    const [isPremiumUser, setIsPremiumUser] = useState(false)
    const [regions, setRegions] = useState([]);
    const [followedIgos, setFollowedIgos] = useState([]);
    const currentLanguage = useCurrentLanguage();
    const isFrenchLanguage = currentLanguage.startsWith("fr");
    const selectedIgoName = selectedIgo2?.name || "";
    const isDefaultIgo = !selectedIgo2 || selectedIgoName === "Sélectionner un Forum global";
    const igoCountryCodes = useMemo(() => {
        const codes = String(selectedIgo2?.country_list || "")
            .split(",")
            .map((code) => code.trim().toLowerCase())
            .filter(Boolean)
        return codes.length > 0 ? new Set(codes) : null
    }, [selectedIgo2?.country_list]);

    // Abonné : tous les pays de l'igo ; achat de pays : intersection avec les pays achetés (countriesToRender)
    const displayedCountries = useMemo(() => {
        if (!isPremiumUser || !igoCountryCodes) return countriesToRender
        return countriesToRender.filter((c) => {
            const code = String(c.code || "").toLowerCase()
            return code === WORLD_COUNTRY_CODE || igoCountryCodes.has(code)
        })
    }, [countriesToRender, igoCountryCodes, isPremiumUser]);
    const regionCountryCodes = useMemo(() => {
        if (!selectedRegion || !Array.isArray(selectedRegion.countries)) return null;
        return new Set(selectedRegion.countries.map((item) => String(item.country_code || "").toLowerCase()));
    }, [selectedRegion]);
    const countriesForSelection = useMemo(() => {
        const available = selectedRegion && regionCountryCodes
            ? displayedCountries.filter((country) => regionCountryCodes.has(String(country.code).toLowerCase()))
            : displayedCountries;
        return [...available].sort((a, b) =>
            (b.code === WORLD_COUNTRY_CODE) - (a.code === WORLD_COUNTRY_CODE)
        );
    }, [displayedCountries, regionCountryCodes, selectedRegion]);

    useEffect(() => {
        let isMounted = true;
        fetchAllRegions()
            .then((response) => {
                const rows = Array.isArray(response) ? response : response?.data;
                if (isMounted && Array.isArray(rows)) setRegions(rows);
            })
            .catch((error) => {
                console.error("Erreur lors du chargement des régions", error);
            });
        return () => {
            isMounted = false;
        };
    }, []);

    useEffect(() => {
        if (!isPremiumUser || !localUser?.id) {
            setFollowedIgos([]);
            return undefined;
        }
        let isMounted = true;
        Promise.all([getIgo(), getIgoFromUser(localUser.id)])
            .then(([allIgo, followed]) => {
                if (!isMounted) return;
                const list = Array.isArray(followed)
                    ? followed
                    : Array.isArray(followed?.data)
                        ? followed.data
                        : Array.isArray(followed?.items)
                            ? followed.items
                            : typeof followed?.igo_id_list === "string"
                                ? followed.igo_id_list.split(",")
                                : [];
                const ids = new Set(
                    list
                        .map((item) => item?.igo_id ?? item?.igoId ?? item?.id ?? item?.igo?.id ?? item)
                        .filter(Boolean)
                        .map((id) => String(id).trim())
                );
                setFollowedIgos(
                    (Array.isArray(allIgo) ? allIgo : []).filter((igo) => ids.has(String(igo.id)))
                );
            })
            .catch((error) => {
                console.error("Erreur lors du chargement des forums suivis", error);
                if (isMounted) setFollowedIgos([]);
            });
        return () => {
            isMounted = false;
        };
    }, [isPremiumUser, localUser?.id]);

    const getCountryDisplayName = (country) => {
        if (!isFrenchLanguage || country.code === WORLD_COUNTRY_CODE) {
            return country.name;
        }

        return frenchCountryDisplayNames.of(country.code.toUpperCase()) || country.name;
    };

    useEffect(() => {
        let isMounted = true;

        const loadCountryBarCountries = async () => {
            if (!localUser?.id) {
                if (isMounted) {
                    setCountriesToRender([])
                    setIsPremiumUser(false)
                }
                return;
            }

            try {
                const premiumResponse = await getCountryPurchasePremium(localUser.id)
                const hasCountryPurchasePremium = premiumResponse?.has_country_purchase_premium === true

                if (hasCountryPurchasePremium) {
                    const purchasedResponse = await getPurchasedCountriesByUser(localUser.id)
                    const purchasedRows = Array.isArray(purchasedResponse)
                        ? purchasedResponse
                        : Array.isArray(purchasedResponse?.data)
                            ? purchasedResponse.data
                            : []

                    const purchasedCodes = new Set(
                        purchasedRows
                            .map((item) => String(item?.country_code || "").toLowerCase())
                            .filter(Boolean)
                    )

                    const purchasedCountries = countries.filter((countryItem) =>
                        purchasedCodes.has(String(countryItem.code || "").toLowerCase())
                    )
                    const countriesToDisplay = countries.filter((countryItem) =>
                        purchasedCodes.has(String(countryItem.code || "").toLowerCase()) ||
                        (purchasedCountries.length > 0 && String(countryItem.code || "").toLowerCase() === WORLD_COUNTRY_CODE)
                    )

                    if (isMounted) {
                        setCountriesToRender(countriesToDisplay)
                        setIsPremiumUser(true)
                    }
                    return;
                }

                const localSubscription = JSON.parse(localStorage.getItem("subscriptions") || "null")
                const hasRealSubscription = Boolean(localSubscription) && !isCountryPurchaseMarkerSubscription(localSubscription)

                if (isMounted) {
                    if (hasRealSubscription) {
                        setCountriesToRender(countries)
                        setIsPremiumUser(true)
                    } else {
                        const signupCountry = countries.find((countryItem) => countryItem.code === localUser?.country)
                        setCountriesToRender(signupCountry ? [signupCountry] : [])
                        setIsPremiumUser(false)
                    }
                }
            } catch (error) {
                if (isMounted) {
                    const localSubscription = JSON.parse(localStorage.getItem("subscriptions") || "null")
                    const hasRealSubscription = Boolean(localSubscription) && !isCountryPurchaseMarkerSubscription(localSubscription)
                    if (hasRealSubscription) {
                        setCountriesToRender(countries)
                        setIsPremiumUser(true)
                    } else {
                        const signupCountry = countries.find((countryItem) => countryItem.code === localUser?.country)
                        setCountriesToRender(signupCountry ? [signupCountry] : [])
                        setIsPremiumUser(false)
                    }
                }
            }
        };

        loadCountryBarCountries();

        return () => {
            isMounted = false;
        };
    }, [localUser?.country, localUser?.id]);

    const getARandomCountry = () => {
        const country = countries[Math.floor(Math.random() * countries.length)]
        //Array.from(document.getElementsByClassName('divli')).find((c) => c.innerText === country.name).scrollIntoView({ behavior: 'smooth'})
        funChangeCountry(country)
    }

    const handleSelectFollowedIgo = (igoElement) => {
        sessionStorage.setItem("search_igo", igoElement.name);
        const formattedLink = String(igoElement.link || "")
            .replace(/^(https?:\/\/)?(www\.)?/, "")
            .replace(/\/$/, "");
        sessionStorage.setItem("search_query", formattedLink + " ");
        sessionStorage.setItem(
            "search_country",
            JSON.stringify(countries.find((c) => c.code === "wd"))
        );
        sessionStorage.setItem("selected_igo", JSON.stringify(igoElement));
        if (props.setSelectedIgo) {
            props.setSelectedIgo(igoElement);
            if (countrySelected?.code !== WORLD_COUNTRY_CODE) {
                // Le changement de pays recharge la page courante, qui relit l'igo depuis sessionStorage
                funChangeCountry(countries.find((c) => c.code === WORLD_COUNTRY_CODE));
            }
            return;
        }
        window.location.href = `/igo/${igoElement.id}`;
    }

    const handleSelectRegion = (region) => {
        props.setSelectedIgo?.({ name: "Sélectionner un Forum global", country_list: "" });
        if (!region) {
            if (onSelectRegion) {
                onSelectRegion(null);
            } else {
                window.location.href = "/wd/what-this-country-talk-about";
            }
            return;
        }
        if (onSelectRegion) {
            onSelectRegion(region);
            return;
        }
        window.location.href = `/wd/what-this-country-talk-about?region_id=${encodeURIComponent(region.id)}`;
    };

    return (
        <div>
            {
                isPremiumUser && (
                    <input type="text" id={isMobile ? "searchCountryMobile" : "searchCountry"} placeholder="Rechercher un pays..." className="ml-4 mt-4 mr-4 mb-2 w-4/5 border-2 border-cyan-600" onKeyUp={() => {
                        var input, filter, div, a, i;
                        input = isMobile ? document.getElementById("searchCountryMobile") : document.getElementById("searchCountry");
                        filter = input.value.toUpperCase();
                        div = isMobile ? document.getElementById("country_box_mobile") : document.getElementById("country_box");
                        a = isMobile ? div.getElementsByClassName("country_selection_mobile") : div.getElementsByClassName("country_selection");

                        for (i = 0; i < a.length; i++) {
                            let txtValue = a[i].textContent || a[i].innerText;
                            if (txtValue.toUpperCase().indexOf(filter) > -1) {
                                a[i].style.display = "";
                            } else {
                                a[i].style.display = "none";
                            }
                        }
                    }}>
                    </input>
                )
            }
            {isPremiumUser && (
                <select
                    className="ml-4 mt-4 mr-4 mb-2 w-4/5 border-2 border-cyan-600"
                    value=""
                    onChange={(e) => {
                        const igo = followedIgos.find((item) => String(item.id) === e.target.value);
                        if (igo) handleSelectFollowedIgo(igo);
                    }}
                >
                    <option value="">Forums suivis</option>
                    {followedIgos.map((igo) => (
                        <option key={`followed-igo-${igo.id}`} value={igo.id}>{igo.label || igo.name}</option>
                    ))}
                </select>
            )}
            <select
                className="ml-4 mb-2 mr-4 w-4/5 border-2 border-cyan-600"
                value={selectedRegion?.id ?? ""}
                onChange={(e) => {
                    const region = regions.find((r) => String(r.id) === e.target.value);
                    handleSelectRegion(region || null);
                }}
            >
                <option value="">Tous les pays</option>
                {regions.map((region) => (
                    <option key={`region-option-${region.id}`} value={region.id}>{region.name}</option>
                ))}
            </select>
            <div className="ulflag">
                <div id={isMobile ? "country_box_mobile" : "country_box"} className={isPremiumUser === false ? "mt-4" : "mt-0"}>
                    {
                        isDefaultIgo && (
                            <>
                                {
                                    isPremiumUser && (
                                        <ul className="divli" onClick={() => { funChangeDisplayMap() }} key="mapdisplay">
                                            <li className={isMobile ? "country_selection_mobile" : "country_selection"} key="mapdisplaychild">
                                                <div className='divstart'>  <img className="startArticle" src={etoille} alt="etoile" /> </div>
                                                <button>Afficher la carte</button>
                                            </li>
                                        </ul>
                                    )
                                }
                                <ul className="divli" onClick={() => { getARandomCountry() }} key="random">
                                    <li className={isMobile ? "country_selection_mobile" : "country_selection"} key="randomchild">
                                        <div className='divstart'>  <img className="startArticle" src={etoille} alt="etoile" /> </div>
                                        <button>Aléatoire</button>
                                    </li>
                                </ul>
                            </>
                        )
                    }
                    {
                        countriesForSelection.map((c) =>
                            <React.Fragment key={`country-${c.code}`}>

                                <ul className="divli" onClick={() => { funChangeCountry(c) }} style={{ backgroundColor: c.code === countrySelected.code ? '#c3e5ef' : "transparent" }} >
                                    {
                                        isPremiumUser && (
                                            <li className={isMobile ? "country_selection_mobile" : "country_selection"} >
                                                <div className='divstart'>  <img className="startArticle" src={etoille} alt="etoile" /> </div>
                                                <img src={process.env.PUBLIC_URL + '/flags/' + c.code + ".svg"} alt={getCountryDisplayName(c)} /><button className={isFrenchLanguage ? "notranslate" : ""} translate={isFrenchLanguage ? "no" : undefined}>{getCountryDisplayName(c)}</button>
                                            </li>
                                        )
                                    }
                                    {
                                        isPremiumUser === false && JSON.parse(localStorage.getItem("user")).country === c.code && (
                                            <li className={isMobile ? "country_selection_mobile" : "country_selection"} >
                                                <div className='divstart'>  <img className="startArticle" src={etoille} alt="etoile" /> </div>
                                                <img src={process.env.PUBLIC_URL + '/flags/' + c.code + ".svg"} alt={getCountryDisplayName(c)} /><button className={isFrenchLanguage ? "notranslate" : ""} translate={isFrenchLanguage ? "no" : undefined}>{getCountryDisplayName(c)}</button>
                                            </li>
                                        )
                                    }
                                </ul>
                            </React.Fragment>
                        )
                    }
                </div>
            </div>
        </div>
    )
}
